import React, { FunctionComponent, useEffect, useReducer, useRef } from 'react'
import { useIntl } from '@/utils/intl'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import ReactSignatureCanvas from 'react-signature-canvas'
import { Obj } from '.'
import { useSearchParams } from 'next/navigation'
import { useMobiApi } from '@/services/mobi-api'
import {
  InstanceNewNumberV2,
  SetSignatureReq
} from '@/services/mobi-api/numberv2/types'
import { useToast } from '@/context/ToastContext'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  TextField
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useLanguage } from '@/context/LanguageContext'
import Loader from '@/components/ui/loader'
import { decodeFromBase64 } from '@/utils/helpers'
import RichText from '@/components/ui/RichText'
import { useCmsApi } from '@/services/cms-api'
import { DigitalConsentData } from '@/services/cms-api/digital-consent/types'
import { resolveImageUrl } from '@/utils/constants'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

const SignatureCanvas = ReactSignatureCanvas as any

interface Props {
  onClick: (objs: Obj) => void
  instanceId?: string
  instance?: InstanceNewNumberV2
}

interface State {
  instance?: InstanceNewNumberV2
  signature?: string
  pdfData?: string
  termsAgreed?: boolean
  signaturePresent?: boolean
  loading?: boolean
  pdfLoading?: boolean
  loadingIDX?: number
  btnDisable?: boolean
  contractMarkdown?: string
  digitalConsent?: DigitalConsentData
  refCode?: string
  signatureDrawen?: boolean
}

const Step3: FunctionComponent<Props> = ({ onClick, instanceId, instance }) => {
  const { translate } = useIntl()
  const searchParams = useSearchParams()
  const { numberv2 } = useMobiApi()
  const { getInstanceV2, doSign } = numberv2
  const { getDigitalConsentData } = useCmsApi()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const signatureRef = useRef<ReactSignatureCanvas>(null)
  const startedAtRef = useRef(Date.now())
  const trackedScrollMilestonesRef = useRef<Record<string, number[]>>({})
  const fetchedInstanceKeyRef = useRef('')

  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      termsAgreed: false,
      signaturePresent: false,
      pdfLoading: true,
      signatureDrawen: false
    }
  )

  const _id = searchParams.get('_id')
  const currentInstance = React.useMemo(() => {
    if (instance === undefined && state.instance === undefined) {
      return undefined
    }

    return {
      ...(instance ?? {}),
      ...(state.instance ?? {}),
      email: state.instance?.email ?? instance?.email,
      rateplan: state.instance?.rateplan ?? instance?.rateplan,
      newNumber: state.instance?.newNumber ?? instance?.newNumber,
      contactInfo:
        (state.instance?.contactInfo?.length ?? 0) > 0
          ? state.instance?.contactInfo
          : instance?.contactInfo,
      contracts: state.instance?.contracts ?? instance?.contracts,
      privacy: state.instance?.privacy ?? instance?.privacy,
      configuratorRes: {
        ...(instance?.configuratorRes ?? {}),
        ...(state.instance?.configuratorRes ?? {}),
        price: {
          ...(instance?.configuratorRes?.price ?? {}),
          ...(state.instance?.configuratorRes?.price ?? {})
        }
      },
      danInfo: {
        ...(instance?.danInfo ?? {}),
        ...(state.instance?.danInfo ?? {})
      }
    }
  }, [instance, state.instance])

  const isPrepaidStars = React.useMemo(() => {
    const rateplan = (currentInstance?.rateplan ?? '').toLowerCase()
    return rateplan.includes('prsta') || rateplan.includes('stars')
  }, [currentInstance?.rateplan])

  const fetchRequest = React.useCallback(async (_id: string): Promise<void> => {
    try {
      setState({ loading: true })
      const res = await getInstanceV2(_id, locale)
      if (res !== undefined && res.error === undefined && res.errorCode === undefined) {
        setState({ instance: res })
        if (res.privacy?.required === true) {
          try {
            const content = await getDigitalConsentData()
            setState({ digitalConsent: content })
          } catch (e) {
            showError(translate('request.failed'))
            console.error(e)
          }
        }
      } else {
        trackNewNumberEvent('step_error', {
          step_name: 'contract_agreement',
          error_type: normalizeTrackingText(res?.error ?? 'contract_fetch_failed')
        })
        showError(locale === 'en' ? (res?.cause?.messageEn ?? (res?.error ?? translate('request.failed'))) : res?.cause?.messageMn ?? (res?.error ?? translate('request.failed')))
      }
    } catch (e) {
      trackNewNumberEvent('step_error', {
        step_name: 'contract_agreement',
        error_type: 'contract_fetch_failed'
      })
      showError(translate('request.failed'))
      console.error(e)
    } finally {
      setState({ loading: false })
    }
  }, [getDigitalConsentData, getInstanceV2, locale, showError, translate])

  useEffect(() => {
    if (instance !== undefined) {
      setState({ instance })
    }
  }, [instance])

  useEffect(() => {
    const targetId = instanceId ?? _id ?? ''
    const fetchKey = `${targetId}:${locale}`
    if (targetId !== '') {
      if (
        instance?._id === targetId &&
        instance?.configuratorRes?.price != null
      ) {
        fetchedInstanceKeyRef.current = fetchKey
        return
      }
      if (fetchedInstanceKeyRef.current === fetchKey) {
        return
      }
      fetchedInstanceKeyRef.current = fetchKey
      void fetchRequest(targetId)
    }
  }, [_id, instance?._id, instance?.configuratorRes?.price, instanceId, fetchRequest, locale])

  useEffect(() => {
    const storedData = sessionStorage.getItem('mc_refCode')
    setState({ refCode: storedData !== null ? storedData : '' })
  }, [])

  useEffect(() => {
    trackNewNumberEvent('page_view_step', {
      step_name: 'contract_agreement'
    })
    startedAtRef.current = Date.now()

    return () => {
      trackNewNumberEvent('time_spent_step', {
        step_name: 'contract_agreement',
        time_spent_seconds: Math.max(
          1,
          Math.round((Date.now() - startedAtRef.current) / 1000)
        )
      })
    }
  }, [])

  if (state.loading === true) {
    return (
      <div className='flex items-center justify-center w-full h-[50vh]'>
        <Loader />
      </div>
    )
  }

  const handleSignature = async (): Promise<void> => {
    if (signatureRef.current !== null && signatureRef.current !== undefined) {
      const dataURL = signatureRef.current.toDataURL()
      const resolvedInstanceId =
        currentInstance?._id ?? instanceId ?? _id ?? ''
      const resolvedConsentId =
        currentInstance?.privacy?.required === true
          ? currentInstance?.privacy?.sessionId ??
            ''
          : ''

      if (resolvedInstanceId === '') {
        trackNewNumberEvent('step_error', {
          step_name: 'contract_agreement',
          error_type: 'missing_instance_id'
        })
        showError(translate('request.failed'))
        return
      }

      try {
        trackNewNumberEvent('click_cta', {
          step_name: 'contract_agreement',
          button_name: 'sign_contract'
        })
        setState({ btnDisable: true })
        const req: SetSignatureReq = {
          instanceId: resolvedInstanceId,
          signature: dataURL,
          consentId: resolvedConsentId,
          lang: locale,
          referral: {
            referralCode: state.refCode !== undefined ? state.refCode : '',
            promoCode: 'REFERRAL-PROMO'
          }
        }
        const res = await doSign(req)
        if (res !== undefined && res.error === undefined) {
          trackNewNumberEvent('sign_contract', {
            step_name: 'contract_agreement'
          })
          trackNewNumberEvent('step_complete', {
            step_name: 'contract_agreement'
          })
          setState({ btnDisable: false })
          onClick({ instanceId: currentInstance?._id ?? resolvedInstanceId })
          sessionStorage.removeItem('mc_refCode')
        } else {
          trackNewNumberEvent('step_error', {
            step_name: 'contract_agreement',
            error_type: normalizeTrackingText(res?.error ?? 'sign_contract_failed')
          })
          setState({ btnDisable: false })
          showError(translate('error'))
        }
      } catch (e) {
        trackNewNumberEvent('step_error', {
          step_name: 'contract_agreement',
          error_type: 'sign_contract_failed'
        })
        showError(translate('error'))
        console.error(e)
      } finally {
        setState({ btnDisable: false })
      }
    }
  }

  const handleBeginSignature = (): void => {
    setState({ signatureDrawen: true })
  }

  const clearSignature = (): void => {
    if (signatureRef.current !== null && signatureRef.current !== undefined) {
      signatureRef.current.clear()
    }
    setState({ signatureDrawen: false })
  }

  const handleContractScroll =
    (contractName: string) =>
      (event: React.UIEvent<HTMLDivElement>): void => {
        const target = event.currentTarget
        const maxScrollTop = target.scrollHeight - target.clientHeight

        if (maxScrollTop <= 0) {
          return
        }

        const percent = Math.round((target.scrollTop / maxScrollTop) * 100)
        const milestones = [25, 50, 75, 100]
        const tracked = trackedScrollMilestonesRef.current[contractName] ?? []

        milestones.forEach((milestone) => {
          if (percent >= milestone && !tracked.includes(milestone)) {
            trackedScrollMilestonesRef.current[contractName] = [
              ...tracked,
              milestone
            ]
            trackNewNumberEvent('scroll_contract', {
              step_name: 'contract_agreement',
              contract_name: normalizeTrackingText(contractName),
              scroll_percent: milestone
            })
          }
        })
      }

  return (
    <div className='w-full h-full flex flex-col justify-center items-center p-4'>
      {state.refCode !== undefined && state.refCode !== ''
        ? (
          <div className='w-full flex bg-surfaceMid rounded-2xl p-2 mb-2'>
            <div className='flex w-full bg-surfaceInverse border border-secondarySoftLow rounded-xl'>
              <TextField
                label={translate('referralPromoCode')}
                className='flex-1 rounded-l-xl text-textHigh'
                value={state.refCode}
                InputProps={{
                  readOnly: true
                }}
                sx={{
                  '& fieldset': {
                    border: 'none'
                  },
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& label.Mui-focused': {
                    color: '#262626'
                  },
                  '& input': {
                    border: 'none',
                    outline: 'none'
                  },
                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                  {
                    display: 'none'
                  },
                  '& input[type="text"]': {
                    MozAppearance: 'textfield'
                  }
                }}
              />
              <Button
                className='m-2'
                disabled
                sx={{
                  borderRadius: '12px',
                  padding: '0 16px',
                  textTransform: 'none'
                }}
              >
                <img
                  src='/icons/verified.svg'
                  height={20}
                  width={20}
                  alt='info'
                />
              </Button>
            </div>
          </div>
          )
        : null}
      <div className='bg-surfaceHigh p-6 md:p-8 rounded-3xl w-full xl:w-1/2 lg:w-3/4 md:w-4/5 flex flex-col gap-4'>
        <span className='text-textHigh font-bold'>
          {translate('agreement.terms')}
        </span>
        {currentInstance?.configuratorRes !== undefined &&
        currentInstance?.configuratorRes !== null
          ? (
            <div className='bg-surfaceMid border border-secondaryHigh rounded-2xl px-4 py-2 flex flex-col gap-2 w-full'>
              <div className='flex flex-col'>
                <span className='text-textMid text-xs md:text-sm'>
                  {translate('number.chosen')}
                </span>
                <span className='text-sm md:text-base font-bold text-textHigh'>
                  {currentInstance?.newNumber}
                </span>
              </div>
              <hr className='border-[#secondaryMidStroke]' />
              {!isPrepaidStars && (
                <>
                  <div className='flex justify-between items-center'>
                    <span className='text-xs md:text-sm text-textMid'>
                      {translate('number.deposit')}
                    </span>
                    <span className='text-xs md:text-sm text-textHigh'>
                      {currentInstance?.configuratorRes?.price?.depositAmount}₮
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-xs md:text-sm text-textMid'>
                      {translate('number.registrationFee')}
                    </span>
                    <span className='text-xs md:text-sm text-textHigh'>
                      {currentInstance?.configuratorRes?.price?.registrationFee}₮
                    </span>
                  </div>
                </>
              )}
              <div className='flex justify-between items-center'>
                <span className='text-xs md:text-sm text-textMid'>
                  {translate('number.simFee')}
                </span>
                <span className='text-xs md:text-sm text-textHigh'>
                  {currentInstance?.configuratorRes?.price?.simAmount}₮
                </span>
              </div>
              {!isPrepaidStars && (
                <>
                  <div className='flex justify-between items-center'>
                    <span className='text-xs md:text-sm text-textMid'>
                      {translate('creditControl')}
                    </span>
                    <span className='text-xs md:text-sm text-textHigh'>
                      {currentInstance?.configuratorRes?.price?.creditLimit === 0
                        ? '-'
                        : `${currentInstance?.configuratorRes?.price?.creditLimit ?? ''}₮`}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-textMid text-xs md:text-sm font-medium'>
                      {translate('totalPrice')}:
                    </span>
                    <span className='text-textHigh text-xs md:text-sm font-bold'>
                      {currentInstance?.configuratorRes?.price?.totalAmount}₮
                    </span>
                  </div>
                </>
              )}
            </div>
            )
          : (
            <></>
            )}
        {(currentInstance?.rateplan === 'POS97' && currentInstance?.configuratorRes?.price?.creditLimit === 0) && (
          <div
            className='w-full box-box-border flex items-center gap-2 border border-[#F4A610] bg-[#FEFCF5] rounded-2xl p-2'
            role='alert'
          >
            <img
              width={32}
              height={30}
              draggable={false}
              src={resolveImageUrl('/uploads/icons/warning.svg')}
              alt='warning'
              className='ml-3'
            />
            <span
              className='text-sm overflow-hidden break-words'
              style={{ color: 'rgba(120, 53, 15, 1)' }}
            >
              {translate('package.usageLimitLevel')}
            </span>
          </div>
        )}
        <Accordion
          className='bg-surfaceMid border border-secondaryHigh rounded-2xl'
          sx={{ '&:before': { height: '0px' } }}
          onChange={(_, expanded) => {
            if (expanded) {
              trackNewNumberEvent('view_contract', {
                step_name: 'contract_agreement',
                contract_name: 'user_info'
              })
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore className='text-textHigh' />}
            aria-controls='panel1-content'
            className='text-textHigh text-sm'
          >
            {translate('order.information.userInfo')}
          </AccordionSummary>
          <AccordionDetails>
            <div className='flex flex-col gap-4'>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('user_info.surname')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {currentInstance?.danInfo?.lastname}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('user_info.name')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {currentInstance?.danInfo?.firstname}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('user_info.register_no')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {currentInstance?.danInfo?.regnum}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('email')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {currentInstance?.email}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('shop.address')}:
                </span>
                <span className='text-textHigh text-xs text-end font-medium w-1/2'>
                  {currentInstance?.danInfo?.passportAddress}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('stars.contact')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {currentInstance?.contactInfo?.[0]?.phoneNumber}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('infoLanguage')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {translate('mongolian')}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-textMid text-xs font-medium'>
                  {translate('infoType')}:
                </span>
                <span className='text-textHigh text-xs font-medium'>
                  {translate('email')}
                </span>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
        {currentInstance?.contracts?.contract !== undefined &&
        currentInstance?.contracts?.contract !== null
          ? (
              currentInstance?.contracts?.contract?.map((item, i) => (
                <Accordion
                  key={i}
                  className='bg-surfaceMid border border-secondaryHigh rounded-2xl'
                  sx={{ '&:before': { height: '0px' } }}
                  onChange={(_, expanded) => {
                    if (expanded) {
                      trackNewNumberEvent('view_contract', {
                        step_name: 'contract_agreement',
                        contract_name: normalizeTrackingText(item.name)
                      })
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore className='text-textHigh' />}
                    aria-controls='panel1-content'
                    className='text-textHigh text-sm'
                  >
                    {item.name}
                  </AccordionSummary>
                  <AccordionDetails
                    className='h-96 overflow-auto'
                    onScroll={handleContractScroll(item.name)}
                  >
                    <RichText
                      textSize='text-sm'
                      content={decodeFromBase64(item.content ?? '') ?? ''}
                    />
                  </AccordionDetails>
                </Accordion>
              ))
            )
          : (
            <></>
            )}
        {currentInstance?.privacy?.required === true
          ? (
            <Accordion
              className='bg-surfaceMid border border-secondaryHigh rounded-2xl'
              sx={{ '&:before': { height: '0px' } }}
              onChange={(_, expanded) => {
                if (expanded) {
                  trackNewNumberEvent('view_contract', {
                    step_name: 'contract_agreement',
                    contract_name: 'digital_consent'
                  })
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore className='text-textHigh' />}
                aria-controls='panel1-content'
                className='text-textHigh text-sm'
              >
                {locale === 'en'
                  ? state.digitalConsent?.titleEn ?? ''
                  : state.digitalConsent?.titleMn ?? ''}
              </AccordionSummary>
              <AccordionDetails onScroll={handleContractScroll('digital_consent')}>
                <div className='p-8 pb-36 md:pb-8'>
                  <RichText
                    content={
                      locale === 'en'
                        ? state.digitalConsent?.bodyEn ?? ''
                        : state.digitalConsent?.bodyMn ?? ''
                    }
                  />
                </div>
              </AccordionDetails>
            </Accordion>
            )
          : (
            <></>
            )}
        <div
          className='flex items-center'
          onClick={() => {
            if (state.termsAgreed !== true) {
              trackNewNumberEvent('accept_contract', {
                step_name: 'contract_agreement'
              })
            }
            setState({ termsAgreed: state.termsAgreed === false })
          }}
        >
          <Checkbox
            color='error'
            checked={state.termsAgreed}
            inputProps={{ 'aria-label': 'controlled' }}
            className='cursor-pointer text-textHigh'
          />
          <p className='text-xs md:text-sm font-medium cursor-pointer hover:text-primaryMain text-textHigh'>
            {translate('number.terms')}
          </p>
        </div>
        <div className='w-full flex justify-center'>
          <SignatureCanvas
            ref={signatureRef}
            onBegin={handleBeginSignature}
            canvasProps={{
              className: 'signature-canvas bg-surfaceMid',
              style: { borderRadius: '16px', width: '100%', height: '150px' }
            }}
          />
        </div>
        <DarkButton
          type='text'
          fullwidth
          onClick={() => {
            void clearSignature()
          }}
        >
          {translate('clear')}
        </DarkButton>
        <DarkButton
          fullwidth
          loading={state.btnDisable}
          disabled={
            state.termsAgreed !== true || state.signatureDrawen !== true || state.btnDisable === true
          }
          onClick={() => {
            void handleSignature()
          }}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}

export default Step3
